import * as React from "react";
import { useState, useEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';
import Downshift from 'downshift';
import { ArrayHelper, Slug } from '@fnv-core/utils';
import { LabelAndPopover } from '../../formcontrols/components/label-and-popover';

interface IOption {
  value: any;
  label: string;
}

export interface IDropdownMultipleProps {
  // The name to be used for form values.
  name: string;
  // The label to display above the component.
  label: string;

  // The options to use in the dropdowns.
  options?: IOption[];
  // Optionally provide a datasource function instead of already available options.
  datasource?: Promise<IOption[]>;
  
  // A function to trigger when the selected options are changed.
  onChange: (selectedOptions: IOption[]) => void;
  // The selected values. If changed, the dropdowns are rendered again based on the selectedValues input.
  selectedValues?: string[];

  // The label to display when no option is selected.
  chooseOptionLabel: string;
  // An optional explanation to display above the field.
  explanation?: string;
  // Indicates if an information icon should be added.
  hasPopover?: boolean;
  // The label to use for the 'add more' button.
  addMoreLabel?: string;

  // An optional maximum number of items that is allowed to be selected.
  maxItems?: number;
  // If true, sort items alphabetically by label.
  sortItems?: boolean;

  // A boolean indicating if the current value is invalid. If true, the errors are displayed.
  isInvalid?: boolean;
  // An optional error (html) to display when isInvalid is truthy.
  error?: string;
}

/**
 * A control for managing multiple dropdowns to handle a single value.
 * NOTE: This code is fully based on the formcontrols/Dropdown.tsx, so there is a lot of double code.
 */
export const DropdownMultipleControl = (props: IDropdownMultipleProps) => {

  if (!props.options && !props.datasource) {
    throw new Error('Either provide options or a datasource that resolves to options.');
  }

  // Can also be 'null', for example when adding a new empty one
  const [selectedOptions, setSelectedOptions] = useState<(IOption | null)[]>([]);
  const [options, setOptions] = useState<IOption[]>([]);
  // Indicates which dropdown search field has the current focus
  const [dropdownSearchIndexHasFocus, setDropdownSearchIndexHasFocus] = useState<number>(null);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  // Load options
  useEffect(() => {
    if (props.options) {
      setOptions(props.options);
    } else {
      const loadOptions = async () => {
        const optionsResult = await props.datasource!;
        setOptions(optionsResult);
      };

      loadOptions().then(() => {
        // nothing, just invoke the promise
      });
    }
  }, [props.options, props.datasource]);

  // Set selected values
  useEffect(() => {
    if (props.selectedValues?.length) {
      const selected = props.selectedValues.map(val => {
        return options.find(opt => opt.value === val);
      });
      setSelectedOptions(selected);
    } else {
      setSelectedOptions([null]);
    }
  }, [props.selectedValues]);

  // External validation
  useEffect(() => {
    if (typeof (props.isInvalid) === 'boolean') {
      setIsInvalid(props.isInvalid);
    }
  }, [props.isInvalid]);

  const selectionChanged = (index: number, selection: IOption) => {
    // It is assumed here that `null` is not in the array
    const copy = [...selectedOptions] as IOption[];
    copy[index] = selection;
    setSelectedOptions(copy);
    props.onChange(copy.filter(c => !!c));
  };

  const onDeleteClick = (index: number) => {
    // It is assumed here that `null` is not in the array
    const copy = [...selectedOptions] as IOption[];
    const deletedItem = copy[index];
    copy.splice(index, 1);
    setSelectedOptions(copy);
    if (deletedItem) {
      props.onChange(copy.filter(c => !!c));
    }
  };

  const optionFilter = (option: IOption, selectedItem: IOption | null, filter: string, index: number) => {
    // Current selected value for this dropdown
    if (selectedItem && option.value === selectedItem.value) {
      return true;
    }

    if (selectedOptions.find(so => so?.value === option.value)) {
      // Already selected in another dropdown
      return false;
    }

    // No filtering
    if (!filter) {
      return true;
    }

    // Not editing the current dropdown filter
    if (dropdownSearchIndexHasFocus !== index) {
      return true;
    }

    // Filter by given filter value
    return option.label.toLowerCase().indexOf(filter.toLowerCase()) > -1;
  };

  const sortOptions = (options: IOption[]) => {
    if (!props.sortItems) {
      return options;
    }

    const sorted = ArrayHelper.sortBy(options, 'label', 'ascending', (item) => item.value === '' ? -1 : null);

    return sorted;
  };

  const filterAndSortOptions = (options: IOption[], selectedItem: IOption | null, inputValue: string, dropdownIndex: number) => {
    const filtered = options.filter((option) => {
      // The first dropdown is allowed to have an empty option if there are no other options selected
      if (option.value === '') {
        return dropdownIndex === 0 && selectedOptions.length <= 1;
      }

      return optionFilter(option, selectedItem, inputValue, dropdownIndex);
    });
    const sorted = sortOptions(filtered);
    return sorted;
  };

  const handleMoreClick = () => {
    if (!props.maxItems || selectedOptions.length < props.maxItems) {
      setSelectedOptions([...selectedOptions, null]);
    }
  };

  const onInputFocus = (index: number) => {
    setDropdownSearchIndexHasFocus(index);
  };

  const onInputBlur = () => {
    setDropdownSearchIndexHasFocus(null);
  };

  const showAddMoreButton = 
    // 1. All existing dropdowns have a value
    !!selectedOptions[selectedOptions.length - 1] 
    // 2. There is no max items configured or the number of dropdowns has nog reached the maxItems yet
    && (!props.maxItems || selectedOptions.length < props.maxItems)
    // 3. There are still values not selected in other dropdowns
    && selectedOptions.length < options.length
    ;

  return (
    <>
      <div className={`form-group form__section-input form-dropdown-multiple-container dropdown-multiple ${Slug.fromString(props.name)}-dropdown-multiple-form-group`} id={props.name + "-container"}>
        <LabelAndPopover 
          fieldId={props.name} 
          label={props.label}
          explanation={props.explanation}
          hasPopover={props.hasPopover}                    
        />
        {selectedOptions.map((selectedOption, dropdownIndex) => {
          // First cannot be deleted
          const deletable = dropdownIndex !== 0;

          return (
            <div className="mb-2 dropdown-multiple__item" key={`multi-dropdown-${dropdownIndex}`}>
                <Downshift
                  selectedItem={selectedOption}
                  itemToString={item => item ? item.label : ''}
                  onChange={selection => selectionChanged(dropdownIndex, selection)}
                >
                  {({
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    isOpen,
                    inputValue,
                    highlightedIndex,
                    selectedItem,
                    getRootProps,
                    getToggleButtonProps,
                  }) => (
                    <div className="form-control form-dropdown__container">
                      <div className="form-dropdown__control" {...getRootProps({} as any, {suppressRefError: true})}>
                        <div className="form-dropdown__value-container">
                          <input {...getInputProps()}
                            placeholder={props.chooseOptionLabel}
                            aria-labelledby={props.name + "-label"}
                            className="form-dropdown__input form-dropdown__input-downshift"
                            onBlur={onInputBlur}
                            onFocus={() => onInputFocus(dropdownIndex)}
                          />
                        </div>
                        <div className="form-dropdown__indicators" {...getToggleButtonProps()}>
                          <div aria-label={'toggle menu'} className="form-dropdown__indicator form-dropdown__dropdown-indicator"></div>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="form-dropdown__menu">
                          <ul {...getMenuProps()} className="form-dropdown__menu-list form-dropdown__menu-list-downshift">
                            {filterAndSortOptions(options, selectedItem, inputValue, dropdownIndex).map((item, dropdownItemIndex) => (
                              <li className={'form-dropdown__option' + (selectedItem?.value === item.value ? ' form-dropdown__option--is-selected' : '')}
                                {...getItemProps({
                                  key: item.value + dropdownItemIndex.toString(),
                                  index: dropdownItemIndex,
                                  item,
                                  style: {
                                    backgroundColor: highlightedIndex === dropdownItemIndex ? 'lightgray' : 'white',
                                  }
                                })}
                              >
                                {item.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </Downshift>
                {deletable && (
                  <div className="dropdown-multiple__actions">
                    <button className="btn" type="button"
                            onClick={() => onDeleteClick(dropdownIndex)}>
                      <i className="icon-trash"></i>
                    </button>
                  </div>
                )}
            </div>
          );
        })}
        {showAddMoreButton &&
          <button type="button" 
                  className="btn btn-primary"
                  onClick={handleMoreClick}>
            <i className="icon-plus"></i> {props.addMoreLabel || 'Toevoegen'}
          </button>
        }
        {dropdownSearchIndexHasFocus != null && isInvalid && (
          <div className="form__invalid-feedback">
            {ReactHtmlParser(props.error)}
          </div>
        )}
      </div>
    </>
  );
};
